import { Container } from '@ingeniorforeningen/figurine-core';
import { useIsColumn } from '../hooks/useIsColumn';
import { useEffect, useState } from 'react';
import { CardList } from '../components/CaseOverview/CardList';
import { TableList } from '../components/CaseOverview/TableList';
import useAppStore from '../store/appStore';
import { ErrorPage } from './ErrorPage';
import { PromotionContent } from '../components/CaseOverview/PromotionContent';
import { CaseOverviewPageHead } from '../components/CaseOverview/CaseOverviewPageHead';
import { CaseState, UiCases } from '../types';
import { readyCasesForUi } from '../helpers/casesHelpers';

const CaseOverview = () => {
  const { isColumn } = useIsColumn();
  const language = useAppStore((store) => store.appLanguage);

  // Fetch methods from store
  const fetchCases = useAppStore((state) => state.fetchCases);
  const fetchPromotions = useAppStore((state) => state.fetchPromotions);

  // Loading states
  const loadingTexts = useAppStore((state) => state.appTexts.loading);
  const loadingCases = useAppStore((state) => state.cases.loading);
  const loadingPromotions = useAppStore((state) => state.promotions.loading);

  // Data
  const texts = useAppStore((state) => state.appTexts.data);
  const { caseOverviewTexts } = texts;
  const cases = useAppStore((state) => state.cases.data);
  const promotions = useAppStore((state) => state.promotions.data);

  // Error states
  const errorTexts = useAppStore((state) => state.appTexts.error);
  const errorPromotions = useAppStore((state) => state.promotions.error);

  // Local state.
  const [uiCases, setUiCases] = useState<UiCases>({
    openCases: null,
    closedCases: null,
    loading: true,
  });

  useEffect(() => {
    fetchCases();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!loadingTexts && texts && !loadingCases && cases) {
      const newUiCases: UiCases = {
        openCases: readyCasesForUi(cases, [CaseState.open], language, texts),
        closedCases: readyCasesForUi(cases, [CaseState.closing, CaseState.closed], language, texts),
        loading: false,
      };
      setUiCases(newUiCases);
    }
  }, [cases, texts, loadingTexts, loadingCases, language]);

  useEffect(() => {
    fetchPromotions(language, 'default');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language]);

  // Error handling
  if (errorTexts) {
    return <ErrorPage />;
  }

  return (
    <Container mt="xl">
      <CaseOverviewPageHead
        loading={loadingTexts}
        header={caseOverviewTexts?.headlines.default.title}
        description={caseOverviewTexts?.headlines.default.description}
        caseButtonText={caseOverviewTexts?.link.newCaseButton.title}
        caseButtonUrl={caseOverviewTexts?.link.newCaseButton.url}
      />
      {isColumn ? (
        <CardList
          loading={loadingCases || loadingTexts || uiCases.loading}
          openCases={uiCases.openCases ?? null}
          closedCases={uiCases.closedCases ?? null}
          noCasesText={caseOverviewTexts?.table.noCases.title}
          closedCasesText={caseOverviewTexts?.table.closedCasesHeadline.title}
          showHistoryText={caseOverviewTexts?.table.closedCasesButton.title}
          hideHistoryText={caseOverviewTexts?.table.hideClosedCasesButton.title}
          language={language}
        />
      ) : (
        <TableList
          loading={loadingCases || loadingTexts || uiCases.loading}
          openCases={uiCases.openCases ?? null}
          closedCases={uiCases.closedCases ?? null}
          noCasesText={caseOverviewTexts?.table.noCases.title}
          closedCasesText={caseOverviewTexts?.table.closedCasesHeadline.title}
          showHistoryText={caseOverviewTexts?.table.closedCasesButton.title}
          hideHistoryText={caseOverviewTexts?.table.hideClosedCasesButton.title}
          language={language}
        />
      )}
      <PromotionContent
        loading={loadingPromotions}
        headerText={caseOverviewTexts?.headlines.default.promotion}
        promotions={promotions}
        error={errorPromotions}
      />
    </Container>
  );
};

export default CaseOverview;
